import React from 'react';
import { Box, Typography } from '@mui/material';
import { CurveFileCard } from './CurveFileCard';
import type { Curve, ParsingError } from '../../../interfaces/toolData';
import type { SidebarFile } from '../../../interfaces/sidebar';

interface CurveInfo {
  name: string;
  unit: string;
  fileType: string;
}

interface FileGroup {
  id: string;
  fileName: string;
  fileType: string | null;
  curves: CurveInfo[];
  error?: ParsingError;
}

interface FileListProps {
  // File data
  filteredData: FileGroup[];
  files: SidebarFile[]; // Workspace files for status mapping
  
  // Search state (for empty search message)
  debouncedSearchTerm: string;
  totalCurves: number;
  totalFiles: number;
  
  // Expansion state
  expandedFiles: Set<string>;
  onToggleExpansion: (fileId: string) => void;
  
  // @dnd-kit drag handlers - CRITICAL for curve-to-chart functionality
  draggedCurve: string | null;
  onDragStart: (curve: CurveInfo, fileType: string) => void;
  onDragEnd: () => void;
  
  // Click handlers
  onCurveClick: (curve: CurveInfo, fileType: string) => void;
  onCurveSelect?: (curve: Curve, fileType: string) => void;
  onFileDelete?: (fileId: string) => void;
}

export const FileList: React.FC<FileListProps> = ({
  filteredData,
  files,
  debouncedSearchTerm,
  totalCurves,
  totalFiles,
  expandedFiles,
  onToggleExpansion,
  draggedCurve,
  onDragStart,
  onDragEnd,
  onCurveClick,
  onCurveSelect,
  onFileDelete
}) => {
  // Show empty search state when no filtered data and there's a search term
  if (filteredData.length === 0 && debouncedSearchTerm) {
    return (
      <Box sx={{ 
        p: 4, 
        textAlign: 'center',
        minHeight: 200,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Typography variant="body2" color="text.secondary">
          No curves found matching "{debouncedSearchTerm}"
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Searched {totalCurves} curves in {totalFiles} files
        </Typography>
      </Box>
    );
  }

  // Render list of CurveFileCard components
  return (
    <>
      {filteredData.map((fileGroup) => {
        // Map workspace file state to CurveFileCard status
        let status: 'uploading' | 'fetching' | 'parsing' | 'success' | 'error';
        
        const workspaceFile = files.find(f => f.filename === fileGroup.fileName);
        
        if (fileGroup.error) {
          status = 'error';
        } else if (workspaceFile) {
          // Use the status from SidebarFile directly
          status = workspaceFile.status;
        } else {
          status = 'success';
        }
        
        return (
          <CurveFileCard
            key={fileGroup.id}
            fileData={{
              ...fileGroup,
              status
            }}
            isExpanded={expandedFiles.has(fileGroup.id)}
            onToggle={onToggleExpansion}
            onCurveSelect={onCurveSelect}
            onFileDelete={onFileDelete}
            // @dnd-kit props - CRITICAL: These must be passed through for drag-to-chart functionality
            draggedCurve={draggedCurve}
            onCurveClick={onCurveClick}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
          />
        );
      })}
    </>
  );
}; 